import React, { useState, useMemo } from 'react';
import { Button, Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { assignRole, getRoleCapabilities } from '../services/authorizationService';
import { Modal, ModalHeader } from '../../../globalComponents/ui/Modal';
import { useRoles } from '../hooks/useAuthorizationData';
import MultiSelectDropdown from '../../../globalComponents/form/dropdowns/MultiSelectDropdown';

const RoleAssignDrawer = ({ show, onHide, userId, onAssigned }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);

  const { data: allRoles, isLoading: loadingRoles } = useRoles();

  const selectedRolesList = useMemo(() => {
    if (!allRoles) return [];
    return allRoles.filter(r => selectedRoleIds.includes(r.id));
  }, [allRoles, selectedRoleIds]);

  const handleAssign = async () => {
    if (!selectedRoleIds.length) return;
    setSaving(true);
    setError(null);
    try {
      await Promise.all(selectedRolesList.map(async (role) => {
        let caps = role.capabilities || [];
        if (!caps.length && role.id) {
          caps = await getRoleCapabilities(role.id);
        }

        return assignRole({
          userId,
          roleId: role.id,
          assignments: caps.map(cap => ({
            capabilityId: cap.id,
            scope: {},
          })),
        });
      }));

      onAssigned?.();
      onHide();
      setSelectedRoleIds([]);
    } catch (e) {
      setError(e?.message || 'Failed to assign roles');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedRoleIds([]);
    setError(null);
    onHide();
  };

  return (
    <Modal show={show} onClose={handleClose} maxWidth={500}>
      <ModalHeader title="Assign Role" subtitle="Grant a predefined set of capabilities" onClose={handleClose} />
      <div style={{ padding: "18px 24px 24px", display: "flex", flexDirection: "column", gap: 14, minHeight: "380px" }}>
        {error && <div className="alert alert-danger p-2 mb-0">{error}</div>}

        <Form.Group>
          <Form.Label className="fw-semibold">Select Role(s)</Form.Label>
          <div style={{ opacity: (loadingRoles || saving) ? 0.6 : 1, pointerEvents: (loadingRoles || saving) ? 'none' : 'auto' }}>
            <MultiSelectDropdown
              options={(allRoles || []).map(r => ({ label: r.name, value: r.id }))}
              selected={selectedRoleIds}
              onChange={setSelectedRoleIds}
              placeholder="-- Choose roles --"
            />
          </div>
        </Form.Group>

        {selectedRolesList.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {selectedRolesList.map(role => (
              <div 
                key={role.id} 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px', 
                  background: '#f1f5f9', 
                  padding: '4px 10px', 
                  borderRadius: '16px',
                  border: '1px solid #e2eaf0',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: '#334155'
                }}
              >
                <FontAwesomeIcon icon={faShieldAlt} style={{ color: '#3f88a5', fontSize: '0.75rem' }} />
                {role.name}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: "auto", paddingTop: 12 }}>
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={saving || selectedRoleIds.length === 0}
          >
            {saving && <Spinner size="sm" className="me-2" />}
            Assign {selectedRoleIds.length > 1 ? 'Roles' : 'Role'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RoleAssignDrawer;